import type { AdminTeamMember, AuditRow, Camera, CodeStatus, DiscountCode, EventMeta, Member, Role, Subscriber } from "./data.js";

const apiEndpoint = import.meta.env.VITE_API_ENDPOINT;

export interface AdminPortalData {
  readonly admin: {
    readonly email: string;
    readonly role: Role;
  };
  readonly subscribers: Subscriber[];
  readonly members: Member[];
  readonly cameras: Camera[];
  readonly events: EventMeta[];
  readonly audit: AuditRow[];
  readonly codes: DiscountCode[];
  readonly adminTeam: AdminTeamMember[];
  readonly dataCoverage?: {
    readonly source?: string;
    readonly subscribers?: number;
    readonly cameras?: number;
    readonly events?: number;
    readonly audit?: number;
    readonly billing?: string;
  };
}

export async function fetchAdminPortalData(idToken: string): Promise<AdminPortalData> {
  if (!apiEndpoint) throw new Error("Admin API endpoint is not configured.");
  let response: Response;
  try {
    response = await fetch(`${apiEndpoint.replace(/\/$/u, "")}/v1/admin/portal-data`, {
      headers: { Authorization: `Bearer ${idToken}` }
    });
  } catch {
    throw new Error("Admin service is temporarily unreachable. Check the network and try again.");
  }
  if (response.status === 401 || response.status === 403) {
    throw new Error("This account is not authorized for the Admin Portal.");
  }
  if (!response.ok) {
    throw new Error(`Admin data could not be loaded (${String(response.status)}).`);
  }
  return await response.json() as AdminPortalData;
}

export async function runSubscriberAction(
  idToken: string,
  subscriberId: string,
  action: "extendTrial" | "endTrial" | "changePlan" | "applyCode" | "cancel" | "reactivate",
  option: string,
  reason: string
): Promise<void> {
  await postAdminAction(idToken, `/v1/admin/subscribers/${encodeURIComponent(subscriberId)}/actions`, { action, option, reason });
}

export async function markCameraForReview(idToken: string, cameraId: string, reason: string): Promise<void> {
  await postAdminAction(idToken, `/v1/admin/cameras/${encodeURIComponent(cameraId)}/review`, { reason });
}

export async function createDiscountCode(idToken: string, code: DiscountCode, reason: string): Promise<void> {
  await postAdminAction(idToken, "/v1/admin/codes", { ...code, reason });
}

export async function updateDiscountCodeStatus(idToken: string, code: string, status: CodeStatus, reason: string): Promise<void> {
  await postAdminAction(idToken, `/v1/admin/codes/${encodeURIComponent(code)}`, { status, reason }, "PATCH");
}

async function postAdminAction(idToken: string, path: string, body: unknown, method = "POST"): Promise<void> {
  if (!apiEndpoint) throw new Error("Admin API endpoint is not configured.");
  let response: Response;
  try {
    response = await fetch(`${apiEndpoint.replace(/\/$/u, "")}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${idToken}`,
        "content-type": "application/json"
      },
      body: JSON.stringify(body)
    });
  } catch {
    throw new Error("Admin service is temporarily unreachable. Check the network and try again.");
  }
  if (response.status === 401 || response.status === 403) {
    throw new Error("This account is not authorized for that Admin Portal action.");
  }
  if (!response.ok) {
    const payload = await response.json().catch(() => undefined) as { readonly message?: string } | undefined;
    throw new Error(payload?.message ?? `Admin action failed (${String(response.status)}).`);
  }
}
